import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import UserCombobox from '../../../util_components/user_combobox/UserCombobox';
import CustomModal from '../../../util_components/custom_modal';
import IgnoredView from './IgnoredView';
import {
  SOURCE_NOT_ACTIVE,
  TARGET_INELIGIBLE,
  apiErrorCode,
  apiErrorReason,
  apiStatus,
  commitReassign,
  endAssignment,
  fetchAdvisors,
  fetchHistory,
  previewReassign
} from './api';
import {
  EMPTY_FILTERS,
  LEVELS,
  MAX_COMMIT_ITEMS,
  PREVIEW_CAP,
  REASSIGN_ACTION,
  SOURCES,
  advisorOptions,
  commitRequests,
  eligibleTargets,
  endNotice,
  endReasonLabel,
  endRequest,
  flagReasons,
  formatTs,
  historyActor,
  isIgnoredView,
  listQuery,
  outcomeLabel,
  previewByAdvisor,
  previewByStudents,
  previewTruncated,
  rangeFromSearch,
  runCommit,
  searchWithView,
  skipReasonLabel,
  sourceLabel,
  studentNames
} from './advisors';
import type { AdvisorsView, DayRange, Filters } from './advisors';
import { SHARED_STYLES } from '../styles';
import type { AdvisorRow, AdvisorSummary, Commit, History, Preview, PreviewItem } from './types';

export type Notify = (type: 'success' | 'error', message: string) => void;

interface Props {
  notify: Notify;
  // Deep link (?tab=advisors&advisor=<uuid>) seeds the advisor filter.
  initialAdvisor?: string;
  onAdvisorChange?: (uuid: string) => void;
  // Deep link (?view=ignored&from=&to=); falls back to the page URL when unset.
  initialView?: AdvisorsView;
  initialRange?: DayRange;
}

interface PreviewState {
  preview: Preview;
  to: string;
  byAdvisor: boolean;
}

const { MUTED, PRIMARY_BTN, SECONDARY_BTN, FIELD } = SHARED_STYLES;

const SUMMARY_COLS = ['Advisor', 'Level', 'Roster', 'SALES role', 'Active students', 'Flags', ''];
const ROW_COLS = [
  '',
  'Student',
  'Advisor',
  'Level',
  'Source',
  'Assigned',
  'Expires',
  'Assigned by',
  ''
];
const HISTORY_COLS = ['Advisor', 'Source', 'Assigned', 'Expires', 'Ended', 'End reason', 'By'];

const dash = (v: string | null | undefined) => v || '—';

const pageSearch = () => (typeof window === 'undefined' ? '' : window.location.search);

function AdvisorsPanel({
  notify,
  initialAdvisor = '',
  onAdvisorChange,
  initialView,
  initialRange
}: Props) {
  const router = useHistory();
  const [view, setView] = useState<AdvisorsView>(
    initialView || (isIgnoredView(pageSearch()) ? 'ignored' : 'assignments')
  );
  const [range, setRange] = useState<DayRange>(() => initialRange || rangeFromSearch(pageSearch()));
  // Ignored-tag rows outlive an assign; remember who was assigned while the Advisors tab is open (REQ 57).
  const [assignedUuids, setAssignedUuids] = useState<ReadonlySet<string>>(new Set());
  const [endTarget, setEndTarget] = useState<AdvisorRow | null>(null);
  const [ending, setEnding] = useState(false);
  const [draft, setDraft] = useState<Filters>({ ...EMPTY_FILTERS, advisor: initialAdvisor });
  const [applied, setApplied] = useState<Filters>({ ...EMPTY_FILTERS, advisor: initialAdvisor });
  const [summary, setSummary] = useState<AdvisorSummary[]>([]);
  const [rows, setRows] = useState<AdvisorRow[]>([]);
  const [nextCursor, setNextCursor] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState<{ forbidden: boolean; reason: string } | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);
  const [previewState, setPreviewState] = useState<PreviewState | null>(null);
  const [result, setResult] = useState<Commit | null>(null);
  // Captured at commit time: the reload drops moved students from the filtered rows.
  const [resultItems, setResultItems] = useState<PreviewItem[]>([]);
  const [history, setHistory] = useState<{
    studentUuid: string;
    studentName: string;
    loading: boolean;
    data: History | null;
    error: string;
  } | null>(null);
  // Drops responses from a superseded load (filter changed mid-flight).
  const loadSeq = useRef(0);

  const load = useCallback(
    (filters: Filters) => {
      loadSeq.current += 1;
      const seq = loadSeq.current;
      setLoading(true);
      return fetchAdvisors(listQuery(filters))
        .then((d) => {
          if (seq !== loadSeq.current) return;
          setSummary(d.summary);
          setRows(d.rows);
          setNextCursor(d.next_cursor);
          setLoadError(null);
        })
        .catch((e: unknown) => {
          if (seq !== loadSeq.current) return;
          const forbidden = apiStatus(e) === 403;
          const reason = forbidden
            ? 'Super user or sales manager only'
            : apiErrorReason(e, 'Failed to load advisors');
          // Never leave the previous filter's rows (and a live "Preview all") under new inputs.
          setSummary([]);
          setRows([]);
          setNextCursor(0);
          // The target box goes blank with the summary, so drop the hidden target and open preview too.
          setTarget('');
          setPreviewState(null);
          setLoadError({ forbidden, reason });
          if (!forbidden) notify('error', reason);
        })
        .finally(() => {
          if (seq === loadSeq.current) setLoading(false);
        });
    },
    [notify]
  );

  // The Not-assigned view loads its own data; skip the unused assignments call.
  useEffect(() => {
    if (view === 'ignored') return;
    load(applied);
  }, [load, applied, view]);

  // Keeps ?view=&from=&to= in the URL so a refresh or shared link lands on the same view.
  const syncUrl = (nextView: AdvisorsView, nextRange: DayRange) => {
    if (!router) return;
    const { pathname, search } = router.location;
    const next = searchWithView(search, nextView, nextRange);
    if (next !== search) router.replace({ pathname, search: next });
  };

  const changeView = (next: AdvisorsView) => {
    setView(next);
    syncUrl(next, range);
  };

  const changeRange = (next: DayRange) => {
    setRange(next);
    syncUrl('ignored', next);
  };

  const apply = (next: Filters) => {
    setDraft(next);
    setApplied(next);
    setSelected(new Set());
    setPreviewState(null);
    setResult(null);
    if (next.advisor !== applied.advisor && onAdvisorChange) onAdvisorChange(next.advisor);
  };

  const reload = () => {
    setSelected(new Set());
    return load(applied);
  };

  const loadMore = () => {
    if (!nextCursor || loadingMore) return;
    const seq = loadSeq.current;
    setLoadingMore(true);
    fetchAdvisors(listQuery(applied, nextCursor))
      .then((d) => {
        if (seq !== loadSeq.current) return;
        setRows((prev) => [...prev, ...d.rows]);
        setNextCursor(d.next_cursor);
      })
      .catch((e: unknown) => notify('error', apiErrorReason(e, 'Failed to load more students')))
      .finally(() => setLoadingMore(false));
  };

  const toggle = (uuid: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });

  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.student_uuid));
  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.student_uuid)));

  const targets = useMemo(() => eligibleTargets(summary), [summary]);
  const advisors = useMemo(() => advisorOptions(summary), [summary]);
  const filteredAdvisor = summary.find((s) => s.cms_user_uuid === applied.advisor);

  const startPreview = (body: ReturnType<typeof previewByStudents>) => {
    if (busy) return;
    setBusy(true);
    setResult(null);
    previewReassign(body)
      .then((preview) =>
        setPreviewState({ preview, to: target, byAdvisor: 'from_cms_user_uuid' in body })
      )
      .catch((e: unknown) => {
        const reason = apiErrorReason(e, 'Failed to preview reassignment');
        notify(
          'error',
          apiErrorCode(e) === TARGET_INELIGIBLE
            ? `Target advisor is not eligible: ${reason}`
            : reason
        );
      })
      .finally(() => setBusy(false));
  };

  const onPreviewSelected = () => startPreview(previewByStudents(Array.from(selected), target));
  const onPreviewAll = () => startPreview(previewByAdvisor(applied.advisor, target));

  const onConfirm = () => {
    if (!previewState || busy) return;
    const { preview, to } = previewState;
    const requests = commitRequests(preview, to);
    if (!requests.length) return;
    setBusy(true);
    runCommit(preview.batch_ref, requests, commitReassign)
      .then(({ commit, error }) => {
        setResultItems(preview.items);
        setResult(commit);
        setPreviewState(null);
        // Any open history may now be stale.
        setHistory(null);
        if (error) {
          notify(
            'error',
            `${apiErrorReason(error, 'Commit failed')}. ${commit.counts.reassigned} reassigned before the error; retry is safe.`
          );
        } else {
          notify('success', `Reassigned ${commit.counts.reassigned} student(s)`);
        }
        return reload();
      })
      .finally(() => setBusy(false));
  };

  const openHistory = (row: AdvisorRow) => {
    setHistory({
      studentUuid: row.student_uuid,
      studentName: row.student_name,
      loading: true,
      data: null,
      error: ''
    });
    fetchHistory(row.student_uuid)
      .then((data) =>
        setHistory((h) =>
          h && h.studentUuid === row.student_uuid ? { ...h, loading: false, data } : h
        )
      )
      .catch((e: unknown) =>
        setHistory((h) =>
          h && h.studentUuid === row.student_uuid
            ? { ...h, loading: false, error: apiErrorReason(e, 'Failed to load history') }
            : h
        )
      );
  };

  // Reload on success and on SOURCE_NOT_ACTIVE; a generic error keeps the modal and list.
  const onEndConfirm = () => {
    if (!endTarget || ending) return;
    const row = endTarget;
    setEnding(true);
    endAssignment(endRequest(row))
      .then((res) => {
        notify('success', endNotice(row, res));
        setEndTarget(null);
        setHistory((h) => (h && h.studentUuid === row.student_uuid ? null : h));
        return reload();
      })
      .catch((e: unknown) => {
        if (apiErrorCode(e) === SOURCE_NOT_ACTIVE) {
          notify('error', 'This assignment already changed');
          setEndTarget(null);
          setHistory((h) => (h && h.studentUuid === row.student_uuid ? null : h));
          return reload();
        }
        notify('error', apiErrorReason(e, 'Failed to end the assignment'));
        return null;
      })
      .finally(() => setEnding(false));
  };

  const summaryEmptyText = () => {
    if (loading) return 'Loading advisors…';
    if (!loadError) return 'No advisors yet.';
    return loadError.forbidden ? loadError.reason : `Summary unavailable: ${loadError.reason}`;
  };

  const renderSummary = () => (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm" aria-label="Advisors summary">
        <thead className="bg-surface-sunken text-content-secondary">
          <tr>
            {SUMMARY_COLS.map((c, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <th key={`${c}-${i}`} className="px-4 py-2 text-left font-medium">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {summary.length === 0 ? (
            <tr>
              <td colSpan={SUMMARY_COLS.length} className="px-4 py-6 text-center" style={MUTED}>
                {summaryEmptyText()}
              </td>
            </tr>
          ) : (
            summary.map((s) => (
              <tr
                key={s.cms_user_uuid}
                className="border-t border-line"
                style={
                  s.cms_user_uuid === applied.advisor
                    ? { background: 'var(--surface-sunken)' }
                    : undefined
                }
              >
                <td className="px-4 py-3 text-left font-medium">{s.name}</td>
                <td className="px-4 py-3 text-left">{dash(s.level)}</td>
                <td className="px-4 py-3 text-left">
                  {s.roster_status === 'LEFT' ? 'Left' : 'Active'}
                </td>
                <td className="px-4 py-3 text-left">{s.has_sales ? 'Yes' : 'No'}</td>
                <td className="px-4 py-3 text-left">{s.active_students}</td>
                <td className="px-4 py-3 text-left">
                  {s.flagged ? (
                    <div className="flex flex-wrap gap-1">
                      {flagReasons(s).map((r) => (
                        <span
                          key={r}
                          className="inline-block rounded-full bg-state-warning-bg px-2 py-0.5 text-xs font-medium text-state-warning-text"
                        >
                          {r}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span style={MUTED}>—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-left">
                  <button
                    type="button"
                    onClick={() => apply({ ...draft, advisor: s.cms_user_uuid })}
                    aria-label={`View students of ${s.name}`}
                    className="rounded-md px-3 py-1.5 text-xs font-medium"
                    style={SECONDARY_BTN}
                  >
                    View students
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  const renderFilters = () => (
    <form
      className="px-5 py-3 flex flex-wrap items-end gap-3 border-b border-line"
      onSubmit={(e) => {
        e.preventDefault();
        apply(draft);
      }}
    >
      <UserCombobox
        id="advisors-filter-advisor"
        label="Advisor"
        placeholder="Any advisor"
        users={advisors}
        value={draft.advisor}
        on_change={(uuid) => apply({ ...draft, advisor: uuid })}
        width={240}
      />
      <label htmlFor="advisors-filter-level" className="text-xs" style={MUTED}>
        Level
        <select
          id="advisors-filter-level"
          value={draft.level}
          onChange={(e) => apply({ ...draft, level: e.target.value as Filters['level'] })}
          className="block text-sm"
          style={FIELD}
        >
          <option value="">Any</option>
          {LEVELS.map((l) => (
            <option key={l} value={l}>
              {l}
            </option>
          ))}
        </select>
      </label>
      <label htmlFor="advisors-filter-source" className="text-xs" style={MUTED}>
        Source
        <select
          id="advisors-filter-source"
          value={draft.source}
          onChange={(e) => apply({ ...draft, source: e.target.value as Filters['source'] })}
          className="block text-sm"
          style={FIELD}
        >
          <option value="">Any</option>
          {SOURCES.map((s) => (
            <option key={s} value={s}>
              {sourceLabel(s)}
            </option>
          ))}
        </select>
      </label>
      <label htmlFor="advisors-filter-days" className="text-xs" style={MUTED}>
        Expiring within (days)
        <input
          id="advisors-filter-days"
          type="number"
          min={1}
          value={draft.expiringWithinDays}
          onChange={(e) => setDraft({ ...draft, expiringWithinDays: e.target.value })}
          className="block text-sm"
          style={{ ...FIELD, width: 110 }}
        />
      </label>
      <label htmlFor="advisors-filter-q" className="text-xs" style={MUTED}>
        Student
        <input
          id="advisors-filter-q"
          type="text"
          placeholder="Name or email"
          value={draft.q}
          onChange={(e) => setDraft({ ...draft, q: e.target.value })}
          className="block text-sm"
          style={{ ...FIELD, width: 200 }}
        />
      </label>
      <button
        type="submit"
        className="rounded-md px-4 py-1.5 text-sm font-medium"
        style={PRIMARY_BTN}
      >
        Search
      </button>
      <button
        type="button"
        onClick={() => apply({ ...EMPTY_FILTERS })}
        className="rounded-md px-4 py-1.5 text-sm font-medium"
        style={SECONDARY_BTN}
      >
        Reset
      </button>
    </form>
  );

  const renderActions = () => (
    <div className="px-5 py-3 flex flex-wrap items-center gap-3 border-b border-line">
      <UserCombobox
        id="advisors-target"
        label="Reassign to"
        placeholder="Reassign to…"
        users={targets}
        value={target}
        on_change={setTarget}
        disabled={busy}
        width={240}
      />
      <button
        type="button"
        onClick={onPreviewSelected}
        disabled={!target || selected.size === 0 || selected.size > MAX_COMMIT_ITEMS || busy}
        className="rounded-md px-4 py-1.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
        style={PRIMARY_BTN}
      >
        {`Preview reassign (${selected.size} selected)`}
      </button>
      {applied.advisor ? (
        <button
          type="button"
          onClick={onPreviewAll}
          disabled={!target || busy}
          className="rounded-md px-4 py-1.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
          style={SECONDARY_BTN}
        >
          {`Preview reassign all from ${filteredAdvisor?.name || 'this advisor'}`}
        </button>
      ) : null}
      {selected.size > MAX_COMMIT_ITEMS ? (
        <span className="text-xs" style={MUTED}>
          {`Select at most ${MAX_COMMIT_ITEMS} students, or reassign everything from one advisor.`}
        </span>
      ) : null}
    </div>
  );

  const renderPreview = () => {
    if (!previewState) return null;
    const { preview, to } = previewState;
    const batches = commitRequests(preview, to).length;
    return (
      <div
        className="px-5 py-4 border-b border-line"
        role="region"
        aria-label="Reassignment preview"
      >
        <h3 className="font-semibold">
          {`Reassign to ${preview.to.name}${preview.to.level ? ` (${preview.to.level})` : ''}`}
        </h3>
        <p className="mt-1 text-sm" style={MUTED}>
          {`${preview.counts.reassign} to reassign, ${preview.counts.skip} skipped.`}
          {batches > 1 ? ` Sent in ${batches} batches.` : ''}
        </p>
        {previewTruncated(preview, previewState.byAdvisor) ? (
          <p className="mt-1 text-sm" style={MUTED}>
            {`Only the first ${PREVIEW_CAP} are shown; preview again after confirming to move the rest.`}
          </p>
        ) : null}
        <div className="overflow-x-auto mt-2">
          <table className="min-w-full text-sm" aria-label="Preview items">
            <thead className="bg-surface-sunken text-content-secondary">
              <tr>
                {['Student', 'From', 'Expires', 'Action'].map((c) => (
                  <th key={c} className="px-4 py-2 text-left font-medium">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {preview.items.map((i) => (
                <tr key={`${i.student_uuid}-${i.row_id}`} className="border-t border-line">
                  <td className="px-4 py-2 text-left">{i.student_name}</td>
                  <td className="px-4 py-2 text-left">{dash(i.from_name)}</td>
                  <td className="px-4 py-2 text-left">{formatTs(i.expires_at)}</td>
                  <td className="px-4 py-2 text-left">
                    {i.action === REASSIGN_ACTION
                      ? 'Reassign'
                      : `Skip: ${skipReasonLabel(i.skip_reason) || i.action}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy || preview.counts.reassign === 0}
            className="rounded-md px-4 py-1.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
            style={PRIMARY_BTN}
          >
            {busy ? 'Reassigning…' : `Confirm reassign (${preview.counts.reassign})`}
          </button>
          <button
            type="button"
            onClick={() => setPreviewState(null)}
            disabled={busy}
            className="rounded-md px-4 py-1.5 text-sm font-medium"
            style={SECONDARY_BTN}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  const renderResult = () => {
    if (!result) return null;
    const names = studentNames(resultItems, rows);
    return (
      <div
        className="px-5 py-4 border-b border-line"
        role="region"
        aria-label="Reassignment result"
      >
        <h3 className="font-semibold">Reassignment result</h3>
        <p className="mt-1 text-sm" style={MUTED}>
          {`${result.counts.reassigned} reassigned, ${result.counts.duplicate} already done, ${result.counts.skipped} skipped.`}
        </p>
        <ul className="mt-2 text-sm">
          {result.results.map((r) => (
            <li key={`${r.student_uuid}-${r.row_id}`}>
              {`${names.get(r.student_uuid) || r.student_uuid}: ${outcomeLabel(r.outcome)}`}
              {r.reason ? ` (${skipReasonLabel(r.reason)})` : ''}
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={() => setResult(null)}
          className="mt-3 rounded-md px-4 py-1.5 text-sm font-medium"
          style={SECONDARY_BTN}
        >
          Dismiss
        </button>
      </div>
    );
  };

  const renderRow = (r: AdvisorRow) => (
    <tr key={r.row_id} className="border-t border-line">
      <td className="px-4 py-3 text-left">
        <input
          type="checkbox"
          aria-label={`Select ${r.student_name}`}
          checked={selected.has(r.student_uuid)}
          onChange={() => toggle(r.student_uuid)}
        />
      </td>
      <td className="px-4 py-3 text-left">
        <span className="font-medium">{r.student_name}</span>
      </td>
      <td className="px-4 py-3 text-left">{r.advisor_name}</td>
      <td className="px-4 py-3 text-left">{dash(r.level)}</td>
      <td className="px-4 py-3 text-left">{sourceLabel(r.source)}</td>
      <td className="whitespace-nowrap px-4 py-3 text-left">{formatTs(r.assigned_at)}</td>
      <td className="whitespace-nowrap px-4 py-3 text-left">{formatTs(r.expires_at)}</td>
      <td className="px-4 py-3 text-left">{dash(r.assigned_by_name)}</td>
      <td className="px-4 py-3 text-left">
        <button
          type="button"
          onClick={() => openHistory(r)}
          aria-label={`History of ${r.student_name}`}
          className="rounded-md px-3 py-1.5 text-xs font-medium"
          style={SECONDARY_BTN}
        >
          History
        </button>
        <button
          type="button"
          onClick={() => setEndTarget(r)}
          aria-label={`End assignment of ${r.student_name}`}
          className="ml-2 rounded-md px-3 py-1.5 text-xs font-medium"
          style={SECONDARY_BTN}
        >
          End assignment
        </button>
      </td>
    </tr>
  );

  const renderRows = () => {
    if (loadError && rows.length === 0) {
      return (
        <div className="px-4 py-8 text-center">
          <div role="alert" style={{ color: 'var(--state-error)' }}>
            {loadError.forbidden
              ? loadError.reason
              : `Could not load advisors: ${loadError.reason}`}
          </div>
          {loadError.forbidden ? null : (
            <button
              type="button"
              onClick={reload}
              disabled={loading}
              className="mt-3 rounded-md px-3 py-1.5 text-xs font-medium"
              style={PRIMARY_BTN}
            >
              {loading ? 'Retrying…' : 'Retry'}
            </button>
          )}
        </div>
      );
    }
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm" aria-label="Active advisor assignments">
          <thead className="bg-surface-sunken text-content-secondary">
            <tr>
              {ROW_COLS.map((c, i) =>
                i === 0 ? (
                  <th key="select" className="px-4 py-2 text-left font-medium">
                    <input
                      type="checkbox"
                      aria-label="Select all loaded"
                      checked={allSelected}
                      onChange={toggleAll}
                      disabled={rows.length === 0}
                    />
                  </th>
                ) : (
                  // eslint-disable-next-line react/no-array-index-key
                  <th key={`${c}-${i}`} className="px-4 py-2 text-left font-medium">
                    {c}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={ROW_COLS.length} className="px-4 py-8 text-center" style={MUTED}>
                  {loading ? 'Loading students…' : 'No students match these filters.'}
                </td>
              </tr>
            ) : (
              rows.map(renderRow)
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const renderHistory = () => {
    if (!history) return null;
    const rowsH = history.data?.rows || [];
    return (
      <div
        className="px-5 py-4 border-t border-line"
        role="region"
        aria-label={`History of ${history.studentName}`}
      >
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">{`Advisor history: ${history.studentName}`}</h3>
          <button
            type="button"
            onClick={() => setHistory(null)}
            className="rounded-md px-3 py-1.5 text-xs font-medium"
            style={SECONDARY_BTN}
          >
            Close history
          </button>
        </div>
        {history.loading ? (
          <p className="mt-2 text-sm" style={MUTED}>
            Loading history…
          </p>
        ) : null}
        {history.error ? (
          <p role="alert" className="mt-2 text-sm" style={{ color: 'var(--state-error)' }}>
            {history.error}
          </p>
        ) : null}
        {history.data ? (
          <div className="overflow-x-auto mt-2">
            <table className="min-w-full text-sm" aria-label="Advisor history">
              <thead className="bg-surface-sunken text-content-secondary">
                <tr>
                  {HISTORY_COLS.map((c) => (
                    <th key={c} className="px-4 py-2 text-left font-medium">
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rowsH.length === 0 ? (
                  <tr>
                    <td
                      colSpan={HISTORY_COLS.length}
                      className="px-4 py-4 text-center"
                      style={MUTED}
                    >
                      No advisor history.
                    </td>
                  </tr>
                ) : (
                  rowsH.map((h) => (
                    <tr key={h.row_id} className="border-t border-line">
                      <td className="px-4 py-2 text-left">{h.advisor_name}</td>
                      <td className="px-4 py-2 text-left">{sourceLabel(h.source)}</td>
                      <td className="whitespace-nowrap px-4 py-2 text-left">
                        {formatTs(h.assigned_at)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-left">
                        {formatTs(h.expires_at)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-left">
                        {formatTs(h.ended_at)}
                      </td>
                      <td className="px-4 py-2 text-left">{dash(endReasonLabel(h.end_reason))}</td>
                      <td className="px-4 py-2 text-left">{historyActor(h)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    );
  };

  const renderViewToggle = () => (
    <div
      className="px-5 py-2 flex gap-2 border-b border-line"
      role="group"
      aria-label="Advisors view"
    >
      {(
        [
          ['assignments', 'Assignments'],
          ['ignored', 'Not assigned']
        ] as const
      ).map(([id, text]) => (
        <button
          key={id}
          type="button"
          aria-pressed={view === id}
          onClick={() => changeView(id)}
          className="rounded-md px-3 py-1.5 text-xs font-medium"
          style={view === id ? PRIMARY_BTN : SECONDARY_BTN}
        >
          {text}
        </button>
      ))}
    </div>
  );

  const renderEndModal = () => (
    <CustomModal
      show={!!endTarget}
      close={() => (ending ? undefined : setEndTarget(null))}
      title="End assignment"
      inner_style={{ width: 'min(480px, 96vw)', borderRadius: 12 }}
    >
      {endTarget ? (
        <div role="alertdialog" aria-label="Confirm end assignment">
          <p className="text-sm">
            {`End ${endTarget.student_name}'s assignment with ${endTarget.advisor_name}? Attribution stops now.`}
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setEndTarget(null)}
              disabled={ending}
              className="rounded-md px-4 py-1.5 text-sm font-medium"
              style={SECONDARY_BTN}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onEndConfirm}
              disabled={ending}
              className="rounded-md px-4 py-1.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
              style={PRIMARY_BTN}
            >
              {ending ? 'Ending…' : 'Yes, end assignment'}
            </button>
          </div>
        </div>
      ) : null}
    </CustomModal>
  );

  return (
    <section
      className="bg-surface-card rounded-lg shadow-sm border border-line"
      style={{ margin: '20px 5%' }}
    >
      <div className="px-5 py-3 border-b border-line flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="font-semibold">Advisors</h2>
          <p className="mt-1 text-xs" style={{ ...MUTED, margin: 0 }}>
            Each student has one sales advisor for 35 days after their first 1-on-1 note. Reassign
            students here, for example before removing someone&apos;s SALES role.
          </p>
        </div>
        <span className="text-xs" style={MUTED}>
          Times in IST
        </span>
      </div>
      {renderViewToggle()}
      {view === 'ignored' ? (
        <IgnoredView
          notify={notify}
          initialRange={range}
          onRangeChange={changeRange}
          assigned={assignedUuids}
          onAssigned={(uuid) => setAssignedUuids((prev) => new Set(prev).add(uuid))}
        />
      ) : (
        <>
          {renderSummary()}
          {renderFilters()}
          {applied.advisor ? (
            <div className="px-5 py-2 text-xs border-b border-line" style={MUTED}>
              {`Showing students of ${filteredAdvisor?.name || applied.advisor}. `}
              <button
                type="button"
                onClick={() => apply({ ...draft, advisor: '' })}
                className="underline"
                style={{ background: 'transparent', border: 'none', margin: 0, padding: 0 }}
              >
                Clear advisor
              </button>
            </div>
          ) : null}
          {renderActions()}
          {renderPreview()}
          {renderResult()}
          {renderRows()}
          {nextCursor > 0 ? (
            <div className="px-5 py-3 border-t border-line">
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                className="rounded-md px-4 py-1.5 text-sm font-medium"
                style={SECONDARY_BTN}
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          ) : null}
          {renderHistory()}
        </>
      )}
      {renderEndModal()}
    </section>
  );
}

export default AdvisorsPanel;
